import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  makeStyles,
  MenuItem,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Tabs,
  Tab,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import Alert from '@material-ui/lab/Alert';
import HistoryIcon from '@material-ui/icons/History';
import { Content, Page } from '@backstage/core-components';
import { useSetTopBarTitle } from '../shell/TopBarTitleContext';
import { useTranslation } from 'react-i18next';
import {
  generateTestCases,
  listRequirementsForTestCaseGeneration,
  submitTestCaseFeedback,
  exportTestCases,
  ExportFormat,
  getTraceability,
} from './api';
import { GenerateTestCaseButton } from './GenerateTestCaseButton';
import {
  GeneratedTestCaseRow,
  RequirementForTestCaseGeneration,
  RequirementGenerationFilter,
  RequirementGenerationStatus,
  TestCaseGenerationCheckpoint,
  TraceabilityRequirement,
} from './types';
import { TestCaseVersionHistoryPanel } from './version-history';
import TraceabilityDashboard from './TraceabilityDashboard';
import { TestCaseFeedbackDialog } from './feedback';
import {
  BATCH_SIZE_OPTIONS,
  BulkGenerationOutcome,
  BulkGenerationProgress,
  BulkGenerationSummary,
  createBulkFailedStatuses,
  createBulkPendingStatuses,
  createBulkPendingStatusesFromIds,
  getActiveBulkGenerationJob,
  mergeGeneratedTestCases,
  resumeBulkTestCaseGeneration,
  runBulkTestCaseGeneration,
} from './batchGeneration';

const useStyles = makeStyles(theme => ({
  card: {
    marginBottom: theme.spacing(3),
  },
  tabs: {
    marginBottom: theme.spacing(2),
  },
  topBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(2),
  },
  generatedTopBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(2),
  },

  generatedActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 700,
  },
  subtle: {
    color: theme.palette.text.secondary,
  },
  tableContainer: {
    maxHeight: 380,
    overflowY: 'auto',
    overflowX: 'auto',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  requirementsTable: {
    minWidth: 1450,
  },
  generatedContainer: {
    maxHeight: 360,
    overflowY: 'auto',
    overflowX: 'auto',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  generatedContainerExpanded: {
    height: 'calc(100vh - 340px)',
    maxHeight: 'calc(100vh - 340px)',
  },
  generatedTable: {
    minWidth: 1700,
  },
  detailsCell: {
    minWidth: 420,
    whiteSpace: 'normal',
  },
  noWrapCell: {
    whiteSpace: 'nowrap',
  },
  generatedTextCell: {
    whiteSpace: 'pre-wrap',
    minWidth: 280,
  },
  actionBar: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(2),
  },
  emptyBox: {
    padding: theme.spacing(3),
  },
  progressContainer: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.default,
  },
  progressHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
  },
  generationStatusCell: {
    minWidth: 140,
  },
  pairedGeneratedTable: {
    width: '100%',
    tableLayout: 'fixed',
  },
  pairedGeneratedValueCell: {
    width: '50%',
    verticalAlign: 'top',
    whiteSpace: 'normal',
    borderRight: `1px solid ${theme.palette.divider}`,

    '&:last-child': {
      borderRight: 0,
    },
  },
  pairedGeneratedCell: {
    padding: 0,
    minWidth: 640,
    verticalAlign: 'top',
  },
  failedStatusChip: {
    color: theme.palette.error.main,
    borderColor: theme.palette.error.main,
  },
}));

function renderGeneratedValue(value: string | string[] | undefined) {
  if (!value) {
    return <Typography variant="body2">—</Typography>;
  }

  if (Array.isArray(value)) {
    return value.map(item => (
      <Typography key={item} variant="body2">
        • {item}
      </Typography>
    ));
  }

  return <Typography variant="body2">{value}</Typography>;
}

const generationCheckpointStorageKey = 'ses-test-case-generation-checkpoint';
const MAX_BULK_REQUIREMENTS = 100;
const MAX_EXPORT_TEST_CASES = 50;

function createSelectionKey(
  requirements: RequirementForTestCaseGeneration[],
): string {
  return requirements
    .map(requirement => requirement.requirementThreadId)
    .sort()
    .join(',');
}

function loadGenerationCheckpoint(
  selectionKey: string,
): TestCaseGenerationCheckpoint | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const stored = window.sessionStorage.getItem(generationCheckpointStorageKey);

  if (!stored) {
    return undefined;
  }

  try {
    const checkpoint = JSON.parse(stored) as TestCaseGenerationCheckpoint;

    return checkpoint.selectionKey === selectionKey ? checkpoint : undefined;
  } catch {
    return undefined;
  }
}

function saveGenerationCheckpoint(checkpoint: TestCaseGenerationCheckpoint) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.setItem(
      generationCheckpointStorageKey,
      JSON.stringify(checkpoint),
    );
  } catch {
    // Checkpoint persistence is optional and must not fail generation.
  }
}

function clearGenerationCheckpoint() {
  if (typeof window === 'undefined') {
    return;
  }

  window.sessionStorage.removeItem(generationCheckpointStorageKey);
}

function normalizeGeneratedItems(
  value: string | string[] | undefined,
): string[] {
  if (Array.isArray(value)) {
    return value;
  }

  if (!value) {
    return [];
  }

  return [value];
}

type TestCaseGenerationPageProps = {
  projectId: string | number;
};

interface TestCaseHistorySelection {
  testCaseId: string;
  testCaseThreadId: string;
}

export const TestCaseGenerationPage = ({
  projectId,
}: TestCaseGenerationPageProps) => {
  const classes = useStyles();
  const { t } = useTranslation('testCases');

  useSetTopBarTitle(
    t('testCaseGeneration.page.title'),
    t('testCaseGeneration.page.subtitle'),
  );

  const [requirements, setRequirements] = useState<
    RequirementForTestCaseGeneration[]
  >([]);
  const [requirementsLoaded, setRequirementsLoaded] = useState(false);
  const [showRequirements, setShowRequirements] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectedGeneratedIds, setSelectedGeneratedIds] = useState<Set<string>>(
    new Set(),
  );
  const [filter, setFilter] = useState<RequirementGenerationFilter>('APPROVED');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatedTestCases, setGeneratedTestCases] = useState<
    GeneratedTestCaseRow[]
  >([]);
  const [generationTotal, setGenerationTotal] = useState(0);
  const [generationCompleted, setGenerationCompleted] = useState(0);
  const [showGenerationProgress, setShowGenerationProgress] = useState(false);
  const [generationInterrupted, setGenerationInterrupted] = useState(false);
  const [requirementGenerationStatuses, setRequirementGenerationStatuses] =
    useState<Record<string, RequirementGenerationStatus>>({});
  const [loadErrorOpen, setLoadErrorOpen] = useState(false);
  const [generationSuccessOpen, setGenerationSuccessOpen] = useState(false);
  const [generationErrorOpen, setGenerationErrorOpen] = useState(false);
  const [validationMessage, setValidationMessage] = useState<string | null>(
    null,
  );

  const [batchSize, setBatchSize] = useState(1);

  const [bulkGenerationSummary, setBulkGenerationSummary] =
    useState<BulkGenerationSummary | null>(null);

  const [bulkWarningOpen, setBulkWarningOpen] = useState(false);

  const bulkGenerationAbortControllerRef = useRef<AbortController | null>(null);

  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [selectedTestCase, setSelectedTestCase] =
    useState<GeneratedTestCaseRow | null>(null);
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [feedbackSuccessOpen, setFeedbackSuccessOpen] = useState(false);
  const [feedbackErrorMessage, setFeedbackErrorMessage] = useState<
    string | null
  >(null);
  const [historySelection, setHistorySelection] =
    useState<TestCaseHistorySelection | null>(null);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const [exportFormat, setExportFormat] = useState<ExportFormat>('markdown');
  const [activeTab, setActiveTab] = useState(0);
  const [exportSuccessOpen, setExportSuccessOpen] = useState(false);
  const [exportErrorOpen, setExportErrorOpen] = useState(false);
  const [coverageByRequirementId, setCoverageByRequirementId] = useState<
    Map<string, TraceabilityRequirement>
  >(new Map());
  const [exportValidationMessage, setExportValidationMessage] = useState<
    string | null
  >(null);

  useEffect(() => {
    if (!requirementsLoaded) {
      return;
    }

    const loadRequirements = async () => {
      setLoading(true);
      setLoadErrorOpen(false);

      const numericProjectId = Number(projectId);

      const activeBulkJob =
        Number.isInteger(numericProjectId) && numericProjectId > 0
          ? getActiveBulkGenerationJob(numericProjectId)
          : undefined;

      setSelectedIds(
        activeBulkJob ? new Set(activeBulkJob.requirementThreadIds) : new Set(),
      );

      setValidationMessage(null);

      try {
        if (!Number.isInteger(numericProjectId) || numericProjectId <= 0) {
          throw new Error(`Invalid project ID: ${projectId}`);
        }

        const result = await listRequirementsForTestCaseGeneration(
          numericProjectId,
          filter,
        );

        setRequirements(result);

        try {
          const traceability = await getTraceability(numericProjectId);

          setCoverageByRequirementId(
            new Map(
              traceability.requirements.map(requirement => [
                requirement.requirementThreadId,
                requirement,
              ]),
            ),
          );
        } catch {
          setCoverageByRequirementId(new Map());
        }

        setShowRequirements(true);
      } catch {
        setLoadErrorOpen(true);
      } finally {
        setLoading(false);
      }
    };

    void loadRequirements();
  }, [filter, projectId, requirementsLoaded]);

  const resetGenerationProgress = () => {
    const hasProgressState =
      showGenerationProgress ||
      generationInterrupted ||
      generationCompleted > 0 ||
      generationTotal > 0 ||
      bulkGenerationSummary !== null ||
      Object.keys(requirementGenerationStatuses).length > 0;

    if (generating || !hasProgressState) {
      return;
    }

    setGenerationInterrupted(false);
    setShowGenerationProgress(false);
    setGenerationCompleted(0);
    setGenerationTotal(0);
    setRequirementGenerationStatuses({});
    setBulkGenerationSummary(null);
    setBulkWarningOpen(false);
  };

  const selectedRequirements = useMemo(
    () =>
      requirements.filter(requirement =>
        selectedIds.has(requirement.requirementThreadId),
      ),
    [requirements, selectedIds],
  );

  const applyBulkProgress = useCallback((progress: BulkGenerationProgress) => {
    setGenerationTotal(progress.summary.total);

    setGenerationCompleted(progress.summary.processed);

    setBulkGenerationSummary(progress.summary);

    setRequirementGenerationStatuses(progress.statuses);

    setGeneratedTestCases(previous =>
      mergeGeneratedTestCases(previous, progress.generatedTestCases),
    );
  }, []);

  const applyBulkOutcome = useCallback((outcome: BulkGenerationOutcome) => {
    setRequirementGenerationStatuses(outcome.statuses);

    setGeneratedTestCases(previous =>
      mergeGeneratedTestCases(previous, outcome.generatedTestCases),
    );

    setGenerationTotal(outcome.summary.total);

    setGenerationCompleted(outcome.summary.processed);

    setBulkGenerationSummary(outcome.summary);

    setGenerationInterrupted(false);

    if (
      outcome.summary.status === 'FAILED' ||
      (outcome.summary.succeeded === 0 && outcome.summary.failed > 0)
    ) {
      setGenerationErrorOpen(true);
    } else if (
      outcome.summary.status === 'PARTIALLY_FAILED' ||
      outcome.summary.failed > 0 ||
      outcome.summary.persistenceFailed > 0
    ) {
      setBulkWarningOpen(true);
    } else {
      setGenerationSuccessOpen(true);
    }

    window.setTimeout(() => {
      setShowGenerationProgress(false);
    }, 2000);
  }, []);

  useEffect(() => {
    const numericProjectId = Number(projectId);

    if (!Number.isInteger(numericProjectId) || numericProjectId <= 0) {
      return undefined;
    }

    const activeBulkJob = getActiveBulkGenerationJob(numericProjectId);

    if (!activeBulkJob) {
      return undefined;
    }

    const abortController = new AbortController();

    bulkGenerationAbortControllerRef.current = abortController;

    setGenerating(true);
    setGenerationInterrupted(false);
    setGenerationErrorOpen(false);
    setGenerationSuccessOpen(false);
    setBulkWarningOpen(false);

    setGenerationTotal(activeBulkJob.requirementThreadIds.length);

    setGenerationCompleted(0);
    setShowGenerationProgress(true);

    setRequirementGenerationStatuses(
      createBulkPendingStatusesFromIds(activeBulkJob.requirementThreadIds),
    );

    setSelectedIds(new Set(activeBulkJob.requirementThreadIds));

    /*
     * Automatically load the requirements so restored per-requirement
     * statuses become visible after a page reload.
     */
    setRequirementsLoaded(true);
    setShowRequirements(true);

    void resumeBulkTestCaseGeneration({
      projectId: numericProjectId,

      onProgress: applyBulkProgress,

      signal: abortController.signal,
    })
      .then(outcome => {
        if (outcome) {
          applyBulkOutcome(outcome);
        }
      })
      .catch(error => {
        if (error instanceof Error && error.name === 'AbortError') {
          return;
        }

        /*
         * Do not mark every requirement as failed here. The backend
         * job may still be running and the saved job can be resumed.
         */
        setGenerationInterrupted(true);
        setGenerationErrorOpen(true);
      })
      .finally(() => {
        if (bulkGenerationAbortControllerRef.current === abortController) {
          bulkGenerationAbortControllerRef.current = null;
        }

        setGenerating(false);
      });

    return () => {
      abortController.abort();
    };
  }, [applyBulkOutcome, applyBulkProgress, projectId]);

  const generationProgress =
    generationTotal > 0
      ? Math.round((generationCompleted / generationTotal) * 100)
      : 0;

  const hasInterruptedGeneration =
    generationInterrupted && generationCompleted < generationTotal;

  const generatedColumns = useMemo(() => {
    const columnMap = new Map<string, string>();

    generatedTestCases.forEach(testCase => {
      testCase.fields.forEach(field => {
        if (!columnMap.has(field.key)) {
          columnMap.set(field.key, field.label);
        }
      });
    });

    const columns = Array.from(columnMap.entries()).map(([key, label]) => ({
      key,
      label,
    }));

    const idColumnIndex = columns.findIndex(
      column =>
        column.key === 'testCaseId' ||
        column.key === 'test_case_id' ||
        column.key === 'id',
    );

    if (idColumnIndex > 0) {
      const [idColumn] = columns.splice(idColumnIndex, 1);

      columns.unshift(idColumn);
    }

    return columns;
  }, [generatedTestCases]);

  useEffect(() => {
    const availableIds = new Set(
      generatedTestCases.map(testCase => testCase.id),
    );

    setSelectedGeneratedIds(previous => {
      const next = new Set(
        Array.from(previous).filter(id => availableIds.has(id)),
      );

      if (
        next.size === previous.size &&
        Array.from(next).every(id => previous.has(id))
      ) {
        return previous;
      }

      return next;
    });
  }, [generatedTestCases]);

  const allVisibleSelected =
    requirements.length > 0 &&
    requirements.every(requirement =>
      selectedIds.has(requirement.requirementThreadId),
    );

  const partiallySelected =
    requirements.some(requirement =>
      selectedIds.has(requirement.requirementThreadId),
    ) && !allVisibleSelected;

  const allGeneratedSelected =
    generatedTestCases.length > 0 &&
    generatedTestCases.every(testCase => selectedGeneratedIds.has(testCase.id));

  const partiallyGeneratedSelected =
    generatedTestCases.some(testCase =>
      selectedGeneratedIds.has(testCase.id),
    ) && !allGeneratedSelected;

  const handleLoadRequirements = () => {
    if (requirementsLoaded) {
      setShowRequirements(true);
      return;
    }

    setRequirementsLoaded(true);
  };

  const handleToggleRequirementsVisibility = () => {
    setShowRequirements(previous => !previous);
  };

  const handleToggleAll = (checked: boolean) => {
    resetGenerationProgress();

    if (checked && requirements.length > MAX_BULK_REQUIREMENTS) {
      setValidationMessage(
        t('testCaseGeneration.messages.selectionLimit', {
          count: MAX_BULK_REQUIREMENTS,
        }),
      );

      return;
    }

    setSelectedIds(
      checked
        ? new Set(
            requirements.map(requirement => requirement.requirementThreadId),
          )
        : new Set(),
    );

    setValidationMessage(null);
  };

  const handleToggleOne = (requirementId: string) => {
    resetGenerationProgress();

    if (
      !selectedIds.has(requirementId) &&
      selectedIds.size >= MAX_BULK_REQUIREMENTS
    ) {
      setValidationMessage(
        t('testCaseGeneration.messages.selectionLimit', {
          count: MAX_BULK_REQUIREMENTS,
        }),
      );

      return;
    }

    setSelectedIds(previous => {
      const next = new Set(previous);

      if (next.has(requirementId)) {
        next.delete(requirementId);
      } else {
        next.add(requirementId);
      }

      return next;
    });

    setValidationMessage(null);
  };

  const handleToggleAllGenerated = (checked: boolean) => {
    if (checked && generatedTestCases.length > MAX_EXPORT_TEST_CASES) {
      setExportValidationMessage(
        t('testCaseGeneration.export.selectionLimit', {
          count: MAX_EXPORT_TEST_CASES,
        }),
      );

      return;
    }

    setSelectedGeneratedIds(
      checked
        ? new Set(generatedTestCases.map(testCase => testCase.id))
        : new Set(),
    );

    setExportValidationMessage(null);
  };

  const handleToggleGenerated = (testCaseId: string) => {
    if (
      !selectedGeneratedIds.has(testCaseId) &&
      selectedGeneratedIds.size >= MAX_EXPORT_TEST_CASES
    ) {
      setExportValidationMessage(
        t('testCaseGeneration.export.selectionLimit', {
          count: MAX_EXPORT_TEST_CASES,
        }),
      );

      return;
    }

    setSelectedGeneratedIds(previous => {
      const next = new Set(previous);

      if (next.has(testCaseId)) {
        next.delete(testCaseId);
      } else {
        next.add(testCaseId);
      }

      return next;
    });

    setExportValidationMessage(null);
  };

  const handleBulkGenerate = async () => {
    const abortController = new AbortController();

    bulkGenerationAbortControllerRef.current?.abort();

    bulkGenerationAbortControllerRef.current = abortController;

    setGenerating(true);
    setValidationMessage(null);
    setGenerationErrorOpen(false);
    setGenerationSuccessOpen(false);
    setGenerationInterrupted(false);
    setBulkGenerationSummary(null);
    setBulkWarningOpen(false);

    setGenerationTotal(selectedRequirements.length);

    setGenerationCompleted(0);
    setShowGenerationProgress(true);
    setSelectedGeneratedIds(new Set());

    setRequirementGenerationStatuses(
      createBulkPendingStatuses(selectedRequirements),
    );

    /*
     * The sequential-generation checkpoint is independent of the
     * asynchronous bulk-generation job.
     */
    clearGenerationCheckpoint();

    try {
      const outcome = await runBulkTestCaseGeneration({
        projectId,
        requirements: selectedRequirements,
        batchSize,

        onProgress: applyBulkProgress,

        signal: abortController.signal,
      });

      applyBulkOutcome(outcome);
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }

      const numericProjectId = Number(projectId);

      const activeBulkJob =
        Number.isInteger(numericProjectId) && numericProjectId > 0
          ? getActiveBulkGenerationJob(numericProjectId)
          : undefined;

      if (activeBulkJob) {
        /*
         * Submission already succeeded and the backend job may still
         * be running. Preserve statuses/results and allow resume.
         */
        setGenerationInterrupted(true);
        setShowGenerationProgress(true);
        setGenerationErrorOpen(true);
      } else {
        /*
         * The POST itself failed before a job was created.
         */
        setRequirementGenerationStatuses(
          createBulkFailedStatuses(selectedRequirements),
        );

        setGenerationCompleted(0);
        setShowGenerationProgress(false);
        setGenerationErrorOpen(true);
      }
    } finally {
      if (bulkGenerationAbortControllerRef.current === abortController) {
        bulkGenerationAbortControllerRef.current = null;
      }

      setGenerating(false);
    }
  };

  const handleGenerate = async () => {
    if (selectedRequirements.length === 0 || generating) {
      return;
    }

    if (selectedRequirements.length > MAX_BULK_REQUIREMENTS) {
      setValidationMessage(
        t('testCaseGeneration.messages.selectionLimit', {
          count: MAX_BULK_REQUIREMENTS,
        }),
      );
      return;
    }

    const approvedTestCases = selectedRequirements.filter(requirement => {
      const coverage = coverageByRequirementId.get(
        requirement.requirementThreadId,
      );

      return coverage?.status === 'APPROVED';
    });

    if (approvedTestCases.length > 0) {
      const ids = approvedTestCases
        .map(requirement => `#${requirement.requirementThreadId}`)
        .join(', ');

      setValidationMessage(
        t('testCaseGeneration.messages.approvedTestCaseExists', { ids }),
      );

      return;
    }

    const notApproved = selectedRequirements.filter(
      requirement => requirement.status !== 'APPROVED',
    );

    if (notApproved.length > 0) {
      const ids = notApproved
        .map(requirement => `#${requirement.requirementThreadId}`)
        .join(', ');

      setValidationMessage(
        notApproved.length === 1
          ? t('testCaseGeneration.messages.notApprovedSingle', { ids })
          : t('testCaseGeneration.messages.notApprovedMultiple', { ids }),
      );

      return;
    }

    if (selectedRequirements.length > 1) {
      await handleBulkGenerate();
      return;
    }

    const selectionKey = createSelectionKey(selectedRequirements);

    const existingCheckpoint = loadGenerationCheckpoint(selectionKey);

    const completedRequirementIds = new Set(
      existingCheckpoint?.completedRequirementIds ?? [],
    );

    let accumulatedTestCases = existingCheckpoint?.generatedTestCases ?? [];

    const pendingRequirements = selectedRequirements.filter(
      requirement =>
        !completedRequirementIds.has(requirement.requirementThreadId),
    );

    const initialStatuses: Record<string, RequirementGenerationStatus> = {};

    selectedRequirements.forEach(requirement => {
      initialStatuses[requirement.requirementThreadId] =
        completedRequirementIds.has(requirement.requirementThreadId)
          ? 'GENERATED'
          : 'PENDING';
    });

    setRequirementGenerationStatuses(initialStatuses);
    setGenerating(true);
    setValidationMessage(null);
    setGenerationErrorOpen(false);
    setGenerationSuccessOpen(false);
    setGenerationInterrupted(false);
    setGenerationTotal(selectedRequirements.length);
    setGenerationCompleted(completedRequirementIds.size);
    setShowGenerationProgress(true);
    setSelectedGeneratedIds(new Set());
    setGeneratedTestCases(accumulatedTestCases);

    try {
      for (const requirement of pendingRequirements) {
        const requirementId = requirement.requirementThreadId;

        setRequirementGenerationStatuses(previous => ({
          ...previous,
          [requirementId]: 'GENERATING',
        }));

        try {
          const response = await generateTestCases([requirement]);

          accumulatedTestCases = mergeGeneratedTestCases(
            accumulatedTestCases,
            response.testCases,
          );

          completedRequirementIds.add(requirementId);

          setGeneratedTestCases(accumulatedTestCases);

          setGenerationCompleted(completedRequirementIds.size);

          setRequirementGenerationStatuses(previous => ({
            ...previous,
            [requirementId]: 'GENERATED',
          }));

          saveGenerationCheckpoint({
            selectionKey,
            completedRequirementIds: Array.from(completedRequirementIds),
            generatedTestCases: accumulatedTestCases,
          });
        } catch {
          setRequirementGenerationStatuses(previous => ({
            ...previous,
            [requirementId]: 'FAILED',
          }));

          setGenerationInterrupted(true);
          setGenerationErrorOpen(true);

          saveGenerationCheckpoint({
            selectionKey,
            completedRequirementIds: Array.from(completedRequirementIds),
            generatedTestCases: accumulatedTestCases,
          });

          return;
        }
      }

      clearGenerationCheckpoint();
      setGenerationInterrupted(false);
      setGenerationSuccessOpen(true);

      window.setTimeout(() => {
        setShowGenerationProgress(false);
      }, 2000);
    } finally {
      setGenerating(false);
    }
  };

  const renderRequirementGenerationStatus = (requirementId: string) => {
    const status = requirementGenerationStatuses[requirementId];

    if (!status) {
      return (
        <Typography variant="body2" className={classes.subtle}>
          —
        </Typography>
      );
    }

    if (status === 'GENERATING') {
      return (
        <Box display="flex" alignItems="center" gridGap={8}>
          <CircularProgress size={14} />

          <Typography variant="body2">
            {t('testCaseGeneration.progress.generatingStatus')}
          </Typography>
        </Box>
      );
    }

    let label = t('testCaseGeneration.progress.pendingStatus');

    if (status === 'GENERATED') {
      label = t('testCaseGeneration.progress.generatedStatus');
    } else if (status === 'FAILED') {
      label = t('testCaseGeneration.progress.failedStatus');
    }

    return (
      <Chip
        size="small"
        color={status === 'GENERATED' ? 'primary' : 'default'}
        variant={status === 'FAILED' ? 'outlined' : 'default'}
        className={status === 'FAILED' ? classes.failedStatusChip : undefined}
        label={label}
      />
    );
  };

  const handleOpenFeedbackDialog = (testCase: GeneratedTestCaseRow) => {
    if (!testCase.testCaseThreadId) {
      setFeedbackErrorMessage(t('testCaseGeneration.feedback.failed'));
      return;
    }

    setSelectedTestCase(testCase);
    setFeedbackDialogOpen(true);
  };

  const handleOpenVersionHistory = (testCase: GeneratedTestCaseRow) => {
    if (!testCase.testCaseThreadId) {
      setFeedbackErrorMessage(t('testCaseGeneration.feedback.failed'));
      return;
    }

    setHistorySelection({
      testCaseId: testCase.id,
      testCaseThreadId: testCase.testCaseThreadId,
    });
  };

  const handleSubmitFeedback = async (instruction: string) => {
    if (
      !selectedTestCase ||
      !selectedTestCase.testCaseThreadId ||
      !instruction.trim()
    ) {
      return;
    }

    const numericProjectId = Number(projectId);

    if (!Number.isInteger(numericProjectId) || numericProjectId <= 0) {
      setFeedbackErrorMessage(t('testCaseGeneration.feedback.failed'));
      return;
    }

    try {
      setSubmittingFeedback(true);
      setFeedbackErrorMessage(null);

      const response = await submitTestCaseFeedback(
        numericProjectId,
        selectedTestCase.testCaseThreadId,
        selectedTestCase.id,
        instruction,
      );

      if (response.instanceStatus !== 'PENDING_REVIEW') {
        throw new Error(t('testCaseGeneration.feedback.failed'));
      }

      setHistoryRefreshKey(previous => previous + 1);

      setFeedbackDialogOpen(false);
      setSelectedTestCase(null);
      setFeedbackSuccessOpen(true);
    } catch (error) {
      setFeedbackErrorMessage(
        error instanceof Error
          ? error.message
          : t('testCaseGeneration.feedback.failed'),
      );
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const renderStatusChip = (status: GeneratedTestCaseRow['status']) => {
    const label =
      status === 'APPROVED'
        ? t('testCaseGeneration.generated.statusApproved')
        : t('testCaseGeneration.generated.statusDraft');

    return (
      <Chip
        size="small"
        color={status === 'APPROVED' ? 'primary' : 'default'}
        label={label}
      />
    );
  };

  const handleExport = async () => {
    try {
      const selectedTestCases = generatedTestCases.filter(testCase =>
        selectedGeneratedIds.has(testCase.id),
      );

      if (selectedTestCases.length === 0) {
        throw new Error(t('testCaseGeneration.export.selectAtLeastOne'));
      }

      if (selectedTestCases.length > MAX_EXPORT_TEST_CASES) {
        setExportValidationMessage(
          t('testCaseGeneration.export.selectionLimit', {
            count: MAX_EXPORT_TEST_CASES,
          }),
        );

        return;
      }

      const missingThreadIds = selectedTestCases.filter(
        testCase => !testCase.testCaseThreadId,
      );

      if (missingThreadIds.length > 0) {
        throw new Error(
          'One or more selected Test Cases do not have a persisted thread ID.',
        );
      }

      const testCaseThreadIds = Array.from(
        new Set(
          selectedTestCases.map(
            testCase => testCase.testCaseThreadId as string,
          ),
        ),
      );

      const numericProjectId = Number(projectId);

      if (!Number.isInteger(numericProjectId) || numericProjectId <= 0) {
        throw new Error(`Invalid project ID: ${projectId}`);
      }

      const result = await exportTestCases(
        numericProjectId,
        testCaseThreadIds,
        exportFormat,
      );

      const url = window.URL.createObjectURL(result.blob);
      const link = document.createElement('a');

      link.href = url;
      link.download = result.filename;

      document.body.appendChild(link);

      try {
        link.click();
      } finally {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }

      setExportSuccessOpen(true);
    } catch {
      setExportErrorOpen(true);
    }
  };

  let progressMessage: string;

  if (bulkGenerationSummary?.status === 'QUEUED') {
    progressMessage = t('testCaseGeneration.batchGeneration.queued');
  } else if (bulkGenerationSummary?.status === 'RUNNING') {
    progressMessage = t('testCaseGeneration.batchGeneration.running', {
      processed: bulkGenerationSummary.processed,
      total: bulkGenerationSummary.total,
    });
  } else if (bulkGenerationSummary) {
    progressMessage = t('testCaseGeneration.batchGeneration.processed', {
      processed: bulkGenerationSummary.processed,
      total: bulkGenerationSummary.total,
    });
  } else if (generationInterrupted) {
    progressMessage = t('testCaseGeneration.progress.interrupted', {
      completed: generationCompleted,
      total: generationTotal,
    });
  } else if (generating && selectedRequirements.length > 1) {
    progressMessage = t('testCaseGeneration.progress.bulkInProgress');
  } else {
    progressMessage = t('testCaseGeneration.progress.status', {
      completed: generationCompleted,
      total: generationTotal,
    });
  }

  return (
    <Page themeId="home">
      <Content>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          className={classes.tabs}
        >
          <Tab label={t('testCaseGeneration.tabs.generate')} />
          <Tab label={t('testCaseGeneration.tabs.dashboard')} />
        </Tabs>

        {activeTab === 1 && <TraceabilityDashboard projectId={projectId} />}

        {activeTab === 0 && (
          <>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card className={classes.card}>
                  <CardContent>
                    <Box className={classes.topBar}>
                      <Box>
                        <Typography
                          variant="h6"
                          className={classes.sectionTitle}
                        >
                          {t('testCaseGeneration.requirements.title')}
                        </Typography>
                        <Typography variant="body2" className={classes.subtle}>
                          {t('testCaseGeneration.requirements.description')}
                        </Typography>
                      </Box>

                      <Box
                        display="flex"
                        alignItems="center"
                        gridGap={16}
                        flexWrap="wrap"
                      >
                        {!requirementsLoaded && (
                          <Button
                            color="primary"
                            variant="contained"
                            disabled={loading}
                            onClick={handleLoadRequirements}
                          >
                            {t('testCaseGeneration.requirements.load')}
                          </Button>
                        )}

                        {requirementsLoaded && (
                          <Button
                            color="primary"
                            variant="outlined"
                            disabled={generating}
                            onClick={handleToggleRequirementsVisibility}
                          >
                            {showRequirements
                              ? t('testCaseGeneration.requirements.hide')
                              : t('testCaseGeneration.requirements.view')}
                          </Button>
                        )}

                        <FormControl size="small" variant="outlined">
                          <InputLabel>
                            {t('testCaseGeneration.filters.label')}
                          </InputLabel>
                          <Select
                            value={filter}
                            label={t('testCaseGeneration.filters.label')}
                            disabled={generating}
                            onChange={event =>
                              setFilter(
                                event.target
                                  .value as RequirementGenerationFilter,
                              )
                            }
                          >
                            <MenuItem value="APPROVED">
                              {t('testCaseGeneration.filters.approved')}
                            </MenuItem>
                            <MenuItem value="ALL">
                              {t('testCaseGeneration.filters.all')}
                            </MenuItem>
                          </Select>
                        </FormControl>
                        <Tooltip
                          title={t(
                            'testCaseGeneration.batchGeneration.helperText',
                          )}
                        >
                          <FormControl
                            size="small"
                            variant="outlined"
                            style={{ minWidth: 160 }}
                          >
                            <InputLabel>
                              {t(
                                'testCaseGeneration.batchGeneration.batchSize',
                              )}
                            </InputLabel>

                            <Select
                              value={batchSize}
                              label={t(
                                'testCaseGeneration.batchGeneration.batchSize',
                              )}
                              disabled={generating}
                              onChange={event => {
                                setBatchSize(Number(event.target.value));

                                resetGenerationProgress();
                              }}
                            >
                              {BATCH_SIZE_OPTIONS.map(option => (
                                <MenuItem key={option} value={option}>
                                  {option === 1
                                    ? t(
                                        'testCaseGeneration.batchGeneration.sequential',
                                      )
                                    : t(
                                        'testCaseGeneration.batchGeneration.parallelOption',
                                        {
                                          count: option,
                                        },
                                      )}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Tooltip>
                      </Box>
                    </Box>

                    {showRequirements && (
                      <>
                        <Box className={classes.tableContainer}>
                          {loading ? (
                            <Box display="flex" justifyContent="center" p={4}>
                              <CircularProgress size={24} />
                            </Box>
                          ) : (
                            <Table
                              stickyHeader
                              size="small"
                              className={classes.requirementsTable}
                            >
                              <TableHead>
                                <TableRow>
                                  <TableCell padding="checkbox">
                                    <Checkbox
                                      checked={allVisibleSelected}
                                      indeterminate={partiallySelected}
                                      disabled={generating}
                                      onChange={event =>
                                        handleToggleAll(event.target.checked)
                                      }
                                    />
                                  </TableCell>
                                  <TableCell className={classes.noWrapCell}>
                                    {t('testCaseGeneration.table.id')}
                                  </TableCell>
                                  <TableCell className={classes.noWrapCell}>
                                    {t('testCaseGeneration.table.requirement')}
                                  </TableCell>
                                  <TableCell className={classes.noWrapCell}>
                                    {t('testCaseGeneration.table.status')}
                                  </TableCell>
                                  <TableCell className={classes.noWrapCell}>
                                    {t(
                                      'testCaseGeneration.table.testCaseStatus',
                                    )}
                                  </TableCell>
                                  <TableCell className={classes.noWrapCell}>
                                    {t(
                                      'testCaseGeneration.table.generationStatus',
                                    )}
                                  </TableCell>
                                  <TableCell className={classes.noWrapCell}>
                                    {t('testCaseGeneration.table.version')}
                                  </TableCell>
                                  <TableCell className={classes.noWrapCell}>
                                    {t('testCaseGeneration.table.externalRef')}
                                  </TableCell>
                                  <TableCell className={classes.noWrapCell}>
                                    {t('testCaseGeneration.table.owner')}
                                  </TableCell>
                                  <TableCell className={classes.detailsCell}>
                                    {t('testCaseGeneration.table.text')}
                                  </TableCell>
                                </TableRow>
                              </TableHead>

                              <TableBody>
                                {requirements.map(requirement => (
                                  <TableRow
                                    key={requirement.requirementThreadId}
                                    hover
                                  >
                                    <TableCell padding="checkbox">
                                      <Checkbox
                                        checked={selectedIds.has(
                                          requirement.requirementThreadId,
                                        )}
                                        disabled={generating}
                                        onChange={() =>
                                          handleToggleOne(
                                            requirement.requirementThreadId,
                                          )
                                        }
                                      />
                                    </TableCell>
                                    <TableCell className={classes.noWrapCell}>
                                      #{requirement.requirementThreadId}
                                    </TableCell>
                                    <TableCell className={classes.noWrapCell}>
                                      {requirement.name ||
                                        t(
                                          'testCaseGeneration.requirements.untitled',
                                        )}
                                    </TableCell>
                                    <TableCell className={classes.noWrapCell}>
                                      <Chip
                                        size="small"
                                        color={
                                          requirement.status === 'APPROVED'
                                            ? 'primary'
                                            : 'default'
                                        }
                                        label={
                                          requirement.status === 'APPROVED'
                                            ? t(
                                                'testCaseGeneration.status.approved',
                                              )
                                            : requirement.status
                                        }
                                      />
                                    </TableCell>
                                    <TableCell className={classes.noWrapCell}>
                                      {(() => {
                                        const coverage =
                                          coverageByRequirementId.get(
                                            requirement.requirementThreadId,
                                          );

                                        if (
                                          !coverage ||
                                          coverage.status === 'NOT_COVERED'
                                        ) {
                                          return '—';
                                        }

                                        return renderStatusChip(
                                          coverage.status === 'APPROVED'
                                            ? 'APPROVED'
                                            : 'DRAFT',
                                        );
                                      })()}
                                    </TableCell>
                                    <TableCell
                                      className={classes.generationStatusCell}
                                    >
                                      {renderRequirementGenerationStatus(
                                        requirement.requirementThreadId,
                                      )}
                                    </TableCell>
                                    <TableCell className={classes.noWrapCell}>
                                      {requirement.approvedVersionId &&
                                      requirement.approvedVersionId !== '0'
                                        ? requirement.approvedVersionId
                                        : '—'}
                                    </TableCell>
                                    <TableCell className={classes.noWrapCell}>
                                      {requirement.externalRef || '—'}
                                    </TableCell>
                                    <TableCell className={classes.noWrapCell}>
                                      {requirement.owner || '—'}
                                    </TableCell>
                                    <TableCell className={classes.detailsCell}>
                                      {requirement.text || '—'}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          )}
                        </Box>

                        {validationMessage && (
                          <Box mt={2}>
                            <Alert severity="warning">
                              {validationMessage}
                            </Alert>
                          </Box>
                        )}

                        {showGenerationProgress && (
                          <Box className={classes.progressContainer}>
                            <Box className={classes.progressHeader}>
                              <Typography variant="body2">
                                {progressMessage}
                              </Typography>

                              <Typography variant="body2">
                                {generationProgress}%
                              </Typography>
                            </Box>

                            <LinearProgress
                              className={classes.progressBar}
                              variant="determinate"
                              value={generationProgress}
                            />

                            {hasInterruptedGeneration && (
                              <Box mt={1}>
                                <Typography variant="caption" color="error">
                                  {t('testCaseGeneration.progress.resumeHint')}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        )}

                        <Box className={classes.actionBar}>
                          <GenerateTestCaseButton
                            selectedCount={selectedRequirements.length}
                            generating={generating}
                            disabled={selectedRequirements.length === 0}
                            resumeAvailable={hasInterruptedGeneration}
                            onClick={handleGenerate}
                          />
                        </Box>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Box className={classes.generatedTopBar}>
                      <Typography variant="h6" className={classes.sectionTitle}>
                        {t('testCaseGeneration.generated.title')}
                      </Typography>

                      <Box className={classes.generatedActions}>
                        <Button
                          color="primary"
                          variant="outlined"
                          disabled={
                            generatedTestCases.length === 0 ||
                            selectedGeneratedIds.size === 0
                          }
                          onClick={handleExport}
                        >
                          {t('testCaseGeneration.export.button')}
                        </Button>

                        <FormControl
                          size="small"
                          variant="outlined"
                          style={{ minWidth: 150 }}
                        >
                          <InputLabel>
                            {t('testCaseGeneration.export.format')}
                          </InputLabel>

                          <Select
                            value={exportFormat}
                            onChange={event =>
                              setExportFormat(
                                event.target.value as ExportFormat,
                              )
                            }
                            label={t('testCaseGeneration.export.format')}
                          >
                            <MenuItem value="markdown">
                              {t('testCaseGeneration.export.markdown')}
                            </MenuItem>

                            <MenuItem value="docx">
                              {t('testCaseGeneration.export.word')}
                            </MenuItem>

                            <MenuItem value="pdf">
                              {t('testCaseGeneration.export.pdf')}
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Box>
                    </Box>

                    {exportValidationMessage && (
                      <Box mb={2}>
                        <Alert severity="warning">
                          {exportValidationMessage}
                        </Alert>
                      </Box>
                    )}

                    <Box
                      className={`${classes.generatedContainer} ${
                        !showRequirements
                          ? classes.generatedContainerExpanded
                          : ''
                      }`}
                    >
                      {generatedTestCases.length === 0 ? (
                        <Box className={classes.emptyBox}>
                          <Typography
                            variant="body2"
                            className={classes.subtle}
                          >
                            {t('testCaseGeneration.generated.placeholder')}
                          </Typography>
                        </Box>
                      ) : (
                        <Table
                          stickyHeader
                          size="small"
                          className={classes.generatedTable}
                        >
                          <TableHead>
                            <TableRow>
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={allGeneratedSelected}
                                  indeterminate={partiallyGeneratedSelected}
                                  onChange={event =>
                                    handleToggleAllGenerated(
                                      event.target.checked,
                                    )
                                  }
                                />
                              </TableCell>

                              {generatedColumns.map(column => {
                                if (
                                  column.key === 'expectedResults' ||
                                  column.key === 'expected_results' ||
                                  column.key === 'expected_result'
                                ) {
                                  return null;
                                }

                                if (column.key === 'steps') {
                                  return (
                                    <TableCell
                                      key="steps-expectedResults"
                                      colSpan={2}
                                      className={classes.noWrapCell}
                                    >
                                      <Box
                                        display="grid"
                                        gridTemplateColumns="1fr 1fr"
                                      >
                                        <Box pr={2}>{column.label}</Box>

                                        <Box pl={2}>
                                          {generatedColumns.find(
                                            item =>
                                              item.key === 'expectedResults' ||
                                              item.key === 'expected_results' ||
                                              item.key === 'expected_result',
                                          )?.label ?? 'Expected Results'}
                                        </Box>
                                      </Box>
                                    </TableCell>
                                  );
                                }

                                return (
                                  <TableCell
                                    key={column.key}
                                    className={classes.noWrapCell}
                                  >
                                    {column.label}
                                  </TableCell>
                                );
                              })}
                              <TableCell className={classes.noWrapCell}>
                                {t('testCaseGeneration.generated.status')}
                              </TableCell>

                              <TableCell className={classes.noWrapCell}>
                                {t('testCaseGeneration.feedback.column')}
                              </TableCell>

                              <TableCell className={classes.noWrapCell}>
                                {t(
                                  'testCaseGeneration.generated.versionHistory',
                                )}
                              </TableCell>
                            </TableRow>
                          </TableHead>

                          <TableBody>
                            {generatedTestCases.map(testCase => (
                              <TableRow key={testCase.id}>
                                <TableCell padding="checkbox">
                                  <Checkbox
                                    checked={selectedGeneratedIds.has(
                                      testCase.id,
                                    )}
                                    onChange={() =>
                                      handleToggleGenerated(testCase.id)
                                    }
                                  />
                                </TableCell>

                                {generatedColumns.map(column => {
                                  if (
                                    column.key === 'expectedResults' ||
                                    column.key === 'expected_results' ||
                                    column.key === 'expected_result'
                                  ) {
                                    return null;
                                  }

                                  if (column.key === 'steps') {
                                    const stepsField = testCase.fields.find(
                                      field => field.key === 'steps',
                                    );

                                    const expectedResultsField =
                                      testCase.fields.find(
                                        field =>
                                          field.key === 'expectedResults' ||
                                          field.key === 'expected_results' ||
                                          field.key === 'expected_result',
                                      );

                                    const steps = normalizeGeneratedItems(
                                      stepsField?.value,
                                    );

                                    const expectedResults =
                                      normalizeGeneratedItems(
                                        expectedResultsField?.value,
                                      );

                                    const rowCount = Math.max(
                                      steps.length,
                                      expectedResults.length,
                                    );

                                    return (
                                      <TableCell
                                        key={`${testCase.id}-steps-expectedResults`}
                                        colSpan={2}
                                        className={classes.pairedGeneratedCell}
                                      >
                                        <Table
                                          size="small"
                                          className={
                                            classes.pairedGeneratedTable
                                          }
                                        >
                                          <TableBody>
                                            {Array.from(
                                              { length: rowCount },
                                              (_, index) => (
                                                <TableRow key={index}>
                                                  <TableCell
                                                    className={
                                                      classes.pairedGeneratedValueCell
                                                    }
                                                  >
                                                    <Typography variant="body2">
                                                      {steps[index] || '—'}
                                                    </Typography>
                                                  </TableCell>

                                                  <TableCell
                                                    className={
                                                      classes.pairedGeneratedValueCell
                                                    }
                                                  >
                                                    <Typography variant="body2">
                                                      {expectedResults[index] ||
                                                        '—'}
                                                    </Typography>
                                                  </TableCell>
                                                </TableRow>
                                              ),
                                            )}
                                          </TableBody>
                                        </Table>
                                      </TableCell>
                                    );
                                  }

                                  const field = testCase.fields.find(
                                    item => item.key === column.key,
                                  );

                                  return (
                                    <TableCell
                                      key={`${testCase.id}-${column.key}`}
                                      className={classes.generatedTextCell}
                                    >
                                      {renderGeneratedValue(field?.value)}
                                    </TableCell>
                                  );
                                })}
                                <TableCell className={classes.noWrapCell}>
                                  {renderStatusChip(testCase.status)}
                                </TableCell>

                                <TableCell className={classes.noWrapCell}>
                                  <IconButton
                                    size="small"
                                    disabled={
                                      !testCase.id ||
                                      !testCase.testCaseThreadId ||
                                      testCase.status === 'APPROVED'
                                    }
                                    onClick={() =>
                                      handleOpenFeedbackDialog(testCase)
                                    }
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>

                                <TableCell className={classes.noWrapCell}>
                                  <Tooltip
                                    title={t(
                                      'testCaseGeneration.generated.versionHistoryHint',
                                    )}
                                  >
                                    <span>
                                      <IconButton
                                        size="small"
                                        disabled={!testCase.testCaseThreadId}
                                        aria-label={t(
                                          'testCaseGeneration.generated.openVersionHistory',
                                          { id: testCase.id },
                                        )}
                                        onClick={() =>
                                          handleOpenVersionHistory(testCase)
                                        }
                                      >
                                        <HistoryIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <TestCaseVersionHistoryPanel
              open={historySelection !== null}
              testCaseId={historySelection?.testCaseId ?? null}
              testCaseThreadId={historySelection?.testCaseThreadId ?? null}
              refreshKey={historyRefreshKey}
              showFeedbackAction={generatedTestCases.some(
                testCase =>
                  testCase.id === historySelection?.testCaseId &&
                  Boolean(testCase.testCaseThreadId) &&
                  testCase.status !== 'APPROVED',
              )}
              onFeedback={() => {
                const testCase = generatedTestCases.find(
                  item => item.id === historySelection?.testCaseId,
                );

                if (testCase) {
                  handleOpenFeedbackDialog(testCase);
                }
              }}
              onClose={() => setHistorySelection(null)}
            />
            <Snackbar
              open={loadErrorOpen}
              autoHideDuration={5000}
              onClose={() => setLoadErrorOpen(false)}
            >
              <Alert severity="error" onClose={() => setLoadErrorOpen(false)}>
                {t('testCaseGeneration.messages.loadFailed')}
              </Alert>
            </Snackbar>

            <Snackbar
              open={generationSuccessOpen}
              autoHideDuration={4000}
              onClose={() => setGenerationSuccessOpen(false)}
            >
              <Alert
                severity="success"
                onClose={() => setGenerationSuccessOpen(false)}
              >
                {t('testCaseGeneration.messages.generated')}
              </Alert>
            </Snackbar>

            <Snackbar
              open={generationErrorOpen}
              autoHideDuration={5000}
              onClose={() => setGenerationErrorOpen(false)}
            >
              <Alert
                severity="error"
                onClose={() => setGenerationErrorOpen(false)}
              >
                {t('testCaseGeneration.messages.failed')}
              </Alert>
            </Snackbar>

            <Snackbar
              open={bulkWarningOpen}
              autoHideDuration={7000}
              onClose={() => setBulkWarningOpen(false)}
            >
              <Alert
                severity="warning"
                onClose={() => setBulkWarningOpen(false)}
              >
                {bulkGenerationSummary
                  ? t('testCaseGeneration.batchGeneration.partialSuccess', {
                      total: bulkGenerationSummary.total,
                      succeeded: bulkGenerationSummary.succeeded,
                      failed: bulkGenerationSummary.failed,
                      persistenceFailed:
                        bulkGenerationSummary.persistenceFailed,
                    })
                  : t('testCaseGeneration.batchGeneration.failed')}
              </Alert>
            </Snackbar>

            <Snackbar
              open={feedbackSuccessOpen}
              autoHideDuration={4000}
              onClose={() => setFeedbackSuccessOpen(false)}
            >
              <Alert
                severity="success"
                onClose={() => setFeedbackSuccessOpen(false)}
              >
                {t('testCaseGeneration.feedback.success')}
              </Alert>
            </Snackbar>

            <Snackbar
              open={feedbackErrorMessage !== null}
              autoHideDuration={5000}
              onClose={() => setFeedbackErrorMessage(null)}
            >
              <Alert
                severity="error"
                onClose={() => setFeedbackErrorMessage(null)}
              >
                {feedbackErrorMessage}
              </Alert>
            </Snackbar>

            <Snackbar
              open={exportSuccessOpen}
              autoHideDuration={4000}
              onClose={() => setExportSuccessOpen(false)}
            >
              <Alert
                severity="success"
                onClose={() => setExportSuccessOpen(false)}
              >
                {t('testCaseGeneration.export.success')}
              </Alert>
            </Snackbar>

            <Snackbar
              open={exportErrorOpen}
              autoHideDuration={5000}
              onClose={() => setExportErrorOpen(false)}
            >
              <Alert severity="error" onClose={() => setExportErrorOpen(false)}>
                {t('testCaseGeneration.export.failed')}
              </Alert>
            </Snackbar>

            <TestCaseFeedbackDialog
              open={feedbackDialogOpen}
              submitting={submittingFeedback}
              onClose={() => {
                if (!submittingFeedback) {
                  setFeedbackDialogOpen(false);
                  setSelectedTestCase(null);
                }
              }}
              onSubmit={handleSubmitFeedback}
            />
          </>
        )}
      </Content>
    </Page>
  );
};

export default TestCaseGenerationPage;
